import React, { useState, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ContainerTask } from '../../types/container';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MakeAppointmentModal, PortAppointmentData, WarehouseAppointmentData } from './modals/MakeAppointmentModal';
import { StoreToYardModal } from './modals/StoreToYardModal';
import { SendToPCCModal } from './modals/SendToPCCModal';
import { PauseTaskModal, PauseTaskData } from './modals/PauseTaskModal';
import { RerouteModal, RerouteData, RouteNode as RerouteRouteNode } from './modals/RerouteModal';
import { ReassignTaskModal, ReassignData } from './modals/ReassignTaskModal';
import { ManualPaymentModal, ManualPaymentData } from './modals/ManualPaymentModal';
import { generateRouteNodes } from '../../utils/routeGenerator';
import { toast } from 'sonner';
import { taskAPI } from '../../services/taskApi';
import { useBookingFlow, bookingToastId, type PartialBookingData } from '../../hooks/useBookingFlow';
import { warehouseApi } from '../../services/warehouseApi';
import { getAllPorts } from '../../services/portApi';
import { yardApi } from '../../services/yardApi';
import { shipperAPI } from '../../services/api';
import { clientApi } from '../../services/clientApi';
import type { Appointment } from '../../types/appointment';

export interface ModalTriggers {
  openPortAppointment: (nodeIndex?: number) => void
  openWarehouseAppointment: () => void
  reschedulePortAppointment: (nodeIndex?: number) => void
  rescheduleWarehouseAppointment: () => void
  openPauseModal: () => void
  openRerouteModal: () => void
  openStoreToYard: () => void
  cancelStoreToYard: () => Promise<void>
  releaseNextLeg: () => Promise<void>
  isReleasingNextLeg: boolean
  openSendToPCC: () => void
  openUpdateEta: () => void
  openReassignModal: () => void
  resumeTask: () => Promise<void>
  isResuming: boolean
  hasOpenNestedModal: () => boolean
}

/**
 * Turn the appointment form's date + 'HH:mm' into an instant.
 * `new Date('YYYY-MM-DDTHH:mm:00')` (no zone) is parsed as *local* time, which
 * is what the operator meant, so toISOString() yields the right instant without
 * any timezone library.
 *
 * Times are optional on the appointment form, so a missing part yields null
 * instead of throwing on an Invalid Date.
 */
const toInstant = (date?: string, time?: string): string | null => {
  if (!date || !time) return null
  const parsed = new Date(`${date}T${time}:00`)
  return isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

type DestinationGroup = 'warehouse' | 'yard' | 'port' | 'shipper' | 'client'

const DESTINATION_GROUPS: DestinationGroup[] = ['warehouse', 'port', 'yard', 'shipper', 'client']

/** Sentinel: this attempt failed, but trying again may work. */
const RETRYABLE_FAILURE = Symbol('retryable')

/** Wait between attempts. Two retries covers a backend restart window (the
 *  cause of the day a dispatcher opened Reroute and every terminal was gone)
 *  without making a genuinely-down server feel hung. */
const DESTINATION_RETRY_DELAYS = [400, 1500]

/**
 * A 4xx is the server's considered answer — not worth repeating. Everything
 * else (no response at all, 5xx, timeout, throttle) is worth one more try.
 */
const isRetryable = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === undefined) return true            // network error / no response
  if (status === 408 || status === 429) return true
  return status >= 500
}

interface DestinationOption {
  id: number
  type: DestinationGroup
  name: string
  shortName?: string
  address?: string
  contact?: string
  phone?: string
  city?: string
  state?: string
}

interface TaskModalFooterProps {
  task: ContainerTask                     // editingTask
  originalTask: ContainerTask             // original task for reroute etc.
  onContainerChange: (updates: Partial<ContainerTask>) => void
  onSaveAndClose: () => Promise<void>
  onCancel: () => void
  // Bypasses the dirty-form discard prompt — used when an external event
  // (extension booking flow completed) wants to dismiss the modal cleanly
  // after we've absorbed all the receipt data.
  onForceClose?: () => void
  skipNextPropSync: React.MutableRefObject<boolean>
  isResuming: boolean
  setIsResuming: (v: boolean) => void
  appointments?: Appointment[]            // from appointments table
  // Called after an action that already wrote to the server from inside this
  // modal (reroute), so the board behind it re-pulls. The modal keeps its own
  // copy in local state; the row underneath does not, and without this it goes
  // on showing the old destination until a manual page reload — which reads as
  // "the reroute didn't work" and gets it done a second time.
  onServerUpdate?: () => void
}

export const TaskModalFooter = forwardRef<ModalTriggers, TaskModalFooterProps>(({
  task,
  originalTask,
  onContainerChange,
  onSaveAndClose,
  onCancel,
  onForceClose,
  skipNextPropSync,
  isResuming,
  setIsResuming,
  appointments,
  onServerUpdate,
}, ref) => {
  // Save button state
  const [isSaving, setIsSaving] = useState(false)

  // Appointment modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [appointmentType, setAppointmentType] = useState<'port' | 'warehouse'>('port')
  const [activeNodeIndex, setActiveNodeIndex] = useState(0)
  const [isSavingAppointment, setIsSavingAppointment] = useState(false)
  // When the extension flow returns an incomplete receipt, we reopen the
  // manual modal with whatever fields the scraper did recover so the
  // dispatcher only has to fill the gaps.
  const [partialBookingPrefill, setPartialBookingPrefill] = useState<PartialBookingData | null>(null)

  // Extension booking flow — happy path runs without ever opening the modal;
  // incomplete/offline paths degrade back to it via the callbacks below.
  const { preflight: bookingPreflight, startBooking } = useBookingFlow({
    onCompleted: () => {
      // 'task_updated' socket already broadcasts the new times; trigger a
      // shallow parent re-sync so the footer's derived fields refresh too.
      onContainerChange({})
      // Auto-dismiss the Task Detail modal once all the receipt data has
      // been absorbed. The success toast lives outside the modal and stays
      // visible. The 1500ms grace gives the parent re-fetch (driven by the
      // task_updated / appointment_updated socket events that fire alongside
      // booking_completed) time to flow back through props before the
      // modal unmounts — without this, an operator who reopens the same
      // task before the refetch round-trips would briefly see stale data.
      if (onForceClose) {
        setTimeout(() => onForceClose(), 1500)
      }
    },
    onIncomplete: ({ partialData }) => {
      setPartialBookingPrefill(partialData)
      setAppointmentType(partialData.facility)
      setActiveNodeIndex(0)
      setShowAppointmentModal(true)
    },
  })

  // Update ETA modal state
  const [showUpdateEtaModal, setShowUpdateEtaModal] = useState(false)
  const [etaResumeDate, setEtaResumeDate] = useState('')
  const [etaResumeTime, setEtaResumeTime] = useState('')

  // Store to Yard / Send to PCC modal state
  const [showStoreToYardModal, setShowStoreToYardModal] = useState(false)
  const [showSendToPCCModal, setShowSendToPCCModal] = useState(false)

  // Pause / Reroute / Reassign modal state
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [showRerouteModal, setShowRerouteModal] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [isPausing, setIsPausing] = useState(false)
  const [isRerouting, setIsRerouting] = useState(false)
  const [isReassigning, setIsReassigning] = useState(false)

  // Cancel Store to Yard state
  const [isCancellingStoreToYard, setIsCancellingStoreToYard] = useState(false)
  const [isReleasingNextLeg, setIsReleasingNextLeg] = useState(false)

  // Manual payment modal state (shown after reassign)
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false)
  const [isCreatingPayment, setIsCreatingPayment] = useState(false)
  const [reassignedTaskId, setReassignedTaskId] = useState<number | null>(null)

  // Destination options (shared by Reroute and Reassign)
  const [destinationOptions, setDestinationOptions] = useState<DestinationOption[]>([])
  // Which groups did not come back. A dropped group used to look exactly like
  // "this company owns none of those", so a single blip silently removed every
  // terminal from the picker and nobody could tell. Failures are now named, and
  // the group can be retried on its own.
  const [failedDestinationGroups, setFailedDestinationGroups] = useState<DestinationGroup[]>([])
  const [destinationsLoading, setDestinationsLoading] = useState(false)
  // Only the newest load may write state — a retry racing the initial load
  // must not be overwritten by the slower loser.
  const destinationsRequestId = useRef(0)

  // Pre-compute route nodes once (used by appointment modal props)
  const routeNodesForAppt = useMemo(() => generateRouteNodes(task), [task])

  /** One attempt. Returns rows, null for a failure worth reporting as-is, or
   *  RETRYABLE_FAILURE when it is worth trying again. */
  const fetchDestinationGroupOnce = React.useCallback(
    async (group: DestinationGroup): Promise<DestinationOption[] | null | typeof RETRYABLE_FAILURE> => {
      try {
        switch (group) {
          case 'warehouse': {
            const res = await warehouseApi.getAll({ limit: 100 })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (res.data || []).map((w: any) => ({
              id: parseInt(w.id),
              type: 'warehouse' as const,
              name: w.warehouseName || w.shortName || '',
              shortName: w.shortName || w.short_name || '',
              address: w.address ? `${w.address.street}, ${w.address.city}, ${w.address.state}` : '',
              contact: w.contacts?.[0]?.name,
              phone: w.contacts?.[0]?.phone,
              city: w.address?.city,
              state: w.address?.state
            }))
          }
          case 'port': {
            const res = await getAllPorts({ limit: 100 })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (res.data || []).map((p: any) => ({
              id: parseInt(p.id),
              type: 'port' as const,
              name: p.portName || p.shortName || '',
              shortName: p.shortName || p.short_name || '',
              address: p.address ? `${p.address.street}, ${p.address.city}, ${p.address.state}` : '',
              contact: p.contacts?.[0]?.name,
              phone: p.contacts?.[0]?.phone,
              city: p.address?.city,
              state: p.address?.state
            }))
          }
          case 'yard': {
            const yards = await yardApi.getAllYards()
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (yards || []).map((y: any) => ({
              id: y.id,
              type: 'yard' as const,
              name: y.name,
              shortName: y.short_name || '',
              address: y.address ? `${y.address}, ${y.city || ''}, ${y.state || ''}` : y.location,
              contact: y.contact_name,
              phone: y.contact_phone,
              city: y.city,
              state: y.state
            }))
          }
          case 'shipper': {
            const res = await shipperAPI.getAll({ limit: 100 })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (res.data || []).map((s: any) => {
              const addr = s.address
              const addressStr = addr
                ? (typeof addr === 'string' ? addr : `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''}`)
                : ''
              return {
                id: typeof s.id === 'string' ? parseInt(s.id) : s.id,
                type: 'shipper' as const,
                name: s.companyName || s.company_name || s.name || '',
                shortName: s.shortName || s.short_name || '',
                address: addressStr,
                contact: s.primaryContact?.name || s.contact_name,
                phone: s.primaryContact?.phone || s.contact_phone,
                city: addr?.city || s.city,
                state: addr?.state || s.state
              }
            })
          }
          case 'client': {
            const res = await clientApi.getAll({ limit: 100 })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (res.data || []).map((c: any) => ({
              id: c.id,
              type: 'client' as const,
              name: c.company_name || c.name || '',
              shortName: c.short_name || '',
              address: c.address ? `${c.address}, ${c.city || ''}, ${c.state || ''}` : '',
              contact: c.primaryContact?.name || c.contact_name,
              phone: c.primaryContact?.phone || c.contact_phone || c.phone,
              city: c.city,
              state: c.state
            }))
          }
        }
      } catch (e) {
        console.error(`[Destinations] Failed to load ${group}s:`, e)
        return isRetryable(e) ? RETRYABLE_FAILURE : null
      }
    },
    []
  )

  /** Fetch one group; never throws. Returns null when the group really failed.
   *  Retries transient failures itself — the blip that emptied the picker was a
   *  backend restart window, and a second attempt a moment later would have made
   *  it invisible. Only a considered answer from the server (a 4xx that is not a
   *  timeout or a throttle) is taken at face value. */
  const fetchDestinationGroup = React.useCallback(
    async (group: DestinationGroup): Promise<DestinationOption[] | null> => {
      for (let attempt = 0; ; attempt++) {
        const rows = await fetchDestinationGroupOnce(group)
        if (rows !== RETRYABLE_FAILURE) return rows
        if (attempt >= DESTINATION_RETRY_DELAYS.length) return null
        await new Promise(r => setTimeout(r, DESTINATION_RETRY_DELAYS[attempt]))
      }
    },
    [fetchDestinationGroupOnce]
  )

  /**
   * Load the named groups in parallel. Groups that succeed replace their own
   * entries; a group that fails leaves whatever was already there alone and is
   * reported back to the picker, so a retry of one group cannot wipe the rest.
   */
  const loadDestinations = React.useCallback(
    async (groups: DestinationGroup[] = DESTINATION_GROUPS) => {
      const requestId = ++destinationsRequestId.current
      setDestinationsLoading(true)

      const results = await Promise.all(groups.map(g => fetchDestinationGroup(g)))

      if (requestId !== destinationsRequestId.current) return  // superseded

      const loaded = new Map<DestinationGroup, DestinationOption[]>()
      const failed: DestinationGroup[] = []
      groups.forEach((group, i) => {
        const rows = results[i]
        if (rows === null) failed.push(group)
        else loaded.set(group, rows)
      })

      setDestinationOptions(prev => [
        // Groups we did not just refetch, and groups whose refetch failed, keep
        // whatever they had.
        ...prev.filter(d => !loaded.has(d.type)),
        ...Array.from(loaded.values()).flat()
      ])
      setFailedDestinationGroups(prev => [
        ...prev.filter(g => !groups.includes(g)),
        ...failed
      ])
      setDestinationsLoading(false)
    },
    [fetchDestinationGroup]
  )

  // Load destinations when Reroute or Reassign modal opens
  React.useEffect(() => {
    if (!showRerouteModal && !showReassignModal) return
    void loadDestinations()
  }, [showRerouteModal, showReassignModal, loadDestinations])

  // Resume handler
  const handleResume = async () => {
    setIsResuming(true)
    try {
      const numericId = parseInt(task.id || task.taskId || '0')
      await taskAPI.resumeTask(numericId)
      onContainerChange({
        status: (task.pausedFromStatus || 'assigned') as ContainerTask['status'],
        pausedAt: undefined,
        pausedFromStatus: undefined,
        pauseReason: undefined,
        pauseLocation: undefined,
        etaResume: undefined
      })
    } catch (error: unknown) {
      console.error('Failed to resume task:', error)
      const axiosErr = error as { response?: { data?: { message?: string } } }
      toast.error(axiosErr.response?.data?.message || 'Failed to resume task')
    } finally {
      setIsResuming(false)
    }
  }

  // Cancel Store to Yard handler
  const handleCancelStoreToYard = async () => {
    if (isCancellingStoreToYard) return
    setIsCancellingStoreToYard(true)
    try {
      const taskId = parseInt(task.id || task.taskId || '0')
      const result = await taskAPI.cancelStoreToYard(taskId)
      skipNextPropSync.current = true
      onContainerChange({
        destinationType: result.destination_type as ContainerTask['destinationType'],
        destinationId: result.destination_id,
        destinationName: result.destination_name || result.warehouse_name || undefined,
        pendingYardStorage: false,
        pendingDeliveryDestinationType: undefined,
        pendingDeliveryDestinationId: undefined,
      } as Partial<ContainerTask>)
    } catch (error: unknown) {
      console.error('Failed to cancel store to yard:', error)
      const axiosErr = error as { response?: { data?: { message?: string } } }
      toast.error(axiosErr.response?.data?.message || 'Failed to cancel store to yard')
    } finally {
      setIsCancellingStoreToYard(false)
    }
  }

  // 提前把下一程放出来，不等这一程跑完 —— 调度今天要排明天的活。
  //
  // 成功不出声（按 UI 规矩），板子靠 socket 自己刷新；只在没办成时说话。
  const handleReleaseNextLeg = async () => {
    if (isReleasingNextLeg) return
    setIsReleasingNextLeg(true)
    try {
      const taskId = parseInt(task.id || task.taskId || '0')
      await taskAPI.releaseNextLeg(taskId)
    } catch (error: unknown) {
      console.error('Failed to release next leg:', error)
      const axiosErr = error as { response?: { data?: { message?: string } } }
      toast.error(axiosErr.response?.data?.message || 'Failed to release the next leg')
    } finally {
      setIsReleasingNextLeg(false)
    }
  }

  // Open manual modal — used as a fallback from the extension flow and as the
  // entry point for reschedule/edit on existing appointments.
  const openManualPortModal = (nodeIndex?: number) => {
    setAppointmentType('port')
    setActiveNodeIndex(nodeIndex ?? 0)
    setShowAppointmentModal(true)
  }
  const openManualWarehouseModal = () => {
    setAppointmentType('warehouse')
    setShowAppointmentModal(true)
  }

  // Nothing about this button changes for ~800ms after it is pressed: the
  // extension ping in preflight has to time out before any toast appears. That
  // silence reads as "my click didn't register", and clicking again used to
  // open a second eModal wizard for the same container. Guarding on a ref
  // rather than state so the second click is refused even if React hasn't
  // re-rendered yet — same reason handleSave uses savingRef.
  const bookingStartRef = useRef(false)

  // Port Book click: preflight → try extension flow → fall back to manual.
  // Warehouse stays manual-only (eModal doesn't book warehouses).
  const handleOpenPortAppointment = async (nodeIndex?: number) => {
    if (bookingStartRef.current) return
    bookingStartRef.current = true

    const node = nodeIndex ?? 0
    const numericId = parseInt(task.id || task.taskId || '0')
    // Claim the task's toast on click, not after preflight, so the operator
    // sees the press land immediately. startBooking reuses this same id, so it
    // turns into the real booking toast rather than adding a second one.
    if (numericId) {
      toast.loading('Checking eModal booking…', { id: bookingToastId(numericId) })
    }

    try {
      setActiveNodeIndex(node)
      setPartialBookingPrefill(null)

      const pre = await bookingPreflight({ facility: 'port', nodeIndex: node })
      if (!pre.ok) {
        if (numericId) toast.dismiss(bookingToastId(numericId))
        // 「请手动填写」那句删了 —— 下一行就把手填弹窗推到脸前，用不着先
        // 弹一条提示再开弹窗。后端给的**具体**原因（这个码头不支持、缺提单号）
        // 屏幕上没有别处交代，有就照样说。
        if (pre.reason !== 'warehouse_not_supported' && pre.message) {
          toast.info(pre.message)
        }
        openManualPortModal(node)
        return
      }

      if (!numericId) {
        openManualPortModal(node)
        return
      }

      // 把节点下标一路送下去：Live Load 的两个码头节点在同一张任务上，
      // 点第二个 Book 的意思是「约还空那一程」。不送的话后端只能按 task_type
      // 猜，会把还空开成提重，回执也会写到提柜那一程上。
      const result = await startBooking(numericId, node)
      if (!result.ok) {
        // Ineligibility (rail ramp, missing BOL, etc.) is expected and not alarming
        // — use info, not error, and pass through the backend's specific reason.
        // 没有具体原因就不出声：手填弹窗紧接着就开了。
        toast.dismiss(bookingToastId(numericId))
        if (result.error) toast.info(result.error)
        openManualPortModal(node)
      }
      // On success the sticky toast + socket events handle the rest; modal stays closed.
    } catch (err) {
      // An unexpected throw must not be the one path that leaves the spinner
      // running — that is the exact failure this whole change is about.
      if (numericId) toast.dismiss(bookingToastId(numericId))
      if ((err as Error)?.message) toast.info((err as Error).message)
      openManualPortModal(node)
    } finally {
      bookingStartRef.current = false
    }
  }

  // Expose triggers via imperative handle
  useImperativeHandle(ref, () => ({
    openPortAppointment: (nodeIndex?: number) => {
      void handleOpenPortAppointment(nodeIndex)
    },
    openWarehouseAppointment: () => {
      setPartialBookingPrefill(null)
      openManualWarehouseModal()
    },
    reschedulePortAppointment: (nodeIndex?: number) => {
      // Edit / reschedule always opens the modal — never restarts the extension flow.
      setPartialBookingPrefill(null)
      openManualPortModal(nodeIndex)
    },
    rescheduleWarehouseAppointment: () => {
      setPartialBookingPrefill(null)
      openManualWarehouseModal()
    },
    openPauseModal: () => setShowPauseModal(true),
    openRerouteModal: () => setShowRerouteModal(true),
    openStoreToYard: () => setShowStoreToYardModal(true),
    cancelStoreToYard: handleCancelStoreToYard,
    releaseNextLeg: handleReleaseNextLeg,
    isReleasingNextLeg,
    openSendToPCC: () => setShowSendToPCCModal(true),
    openUpdateEta: () => setShowUpdateEtaModal(true),
    openReassignModal: () => setShowReassignModal(true),
    resumeTask: handleResume,
    isResuming,
    hasOpenNestedModal: () =>
      showAppointmentModal || showPauseModal || showRerouteModal ||
      showReassignModal || showStoreToYardModal || showSendToPCCModal || showUpdateEtaModal ||
      showManualPaymentModal
  }))

  // Save handler (ref guard prevents double-submit even if React hasn't re-rendered yet)
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (savingRef.current) return
    savingRef.current = true
    setIsSaving(true)
    try {
      await onSaveAndClose()
    } catch (error: unknown) {
      console.error('保存失败:', error)
      toast.error(error instanceof Error ? error.message : "Couldn't save this task — nothing was changed.")
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return (
    <>
      {/* Footer Bar */}
      <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
        {/*
         * 这里原来有一个绿色的 Dispatch 按钮，onClick 里只有一句
         * `toast.success('Dispatched container …')` —— **一个接口都没调**。
         * 点下去弹一条绿字，车一辆没派，而调度完全没有理由怀疑它。
         *
         * 派车真正的入口在派单板上（拖到司机卡 → Confirm All，走
         * batchAssignDriver，还要过预约和车牌那两道门禁）。任务详情里没有
         * 选司机的地方，这颗按钮从来就没有能派的对象 —— 所以是删掉，
         * 不是接上。留着这个空 div 是为了保住 justify-between 的左端。
         */}
        <div />
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all font-medium shadow-lg shadow-blue-500/30 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Make Appointment Modal */}
      <MakeAppointmentModal
        open={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false)
          // Drop any extension-receipt prefill so the next open doesn't
          // reuse stale partial data.
          setPartialBookingPrefill(null)
        }}
        type={appointmentType}
        locationName={appointmentType === 'port'
          ? (() => {
              if (activeNodeIndex > 0) {
                const targetNode = routeNodesForAppt[activeNodeIndex]
                if (targetNode?.name) return targetNode.name
              }
              return task.portShortName || task.portName || 'Port'
            })()
          : (task.warehouseShortName || task.warehouseName || task.warehouse || 'Warehouse')
        }
        locationCity={appointmentType === 'port'
          ? (() => {
              if (activeNodeIndex > 0) {
                const targetNode = routeNodesForAppt[activeNodeIndex]
                if (targetNode?.city) return targetNode.city
              }
              return [task.portCity, task.portState].filter(Boolean).join(', ')
            })()
          : [task.warehouseCity, task.warehouseState].filter(Boolean).join(', ')
        }
        source={(() => {
          // Surface the existing appointment's source in the modal header so
          // dispatchers know whether this record came from eModal or manual entry.
          const appt = appointments?.find(a =>
            a.facilityType === appointmentType && a.status === 'scheduled'
          )
          return appt?.source || null
        })()}
        initialData={appointmentType === 'port' ? (() => {
          // If the extension flow returned an incomplete receipt, prefer those
          // fields over anything already on the task/appointments (the
          // extension-booked values are the dispatcher's most recent intent).
          // MakeAppointmentModal expects startTime/endTime as ISO datetime
          // strings (it feeds them to `new Date(...)`), so synthesize those
          // from the separate date/HH:mm parts the receipt parser returns.
          if (partialBookingPrefill && partialBookingPrefill.facility === 'port') {
            const d = partialBookingPrefill.date
            const toIso = (hm: string | null) => (d && hm ? `${d}T${hm}:00` : undefined)
            return {
              date: d || undefined,
              startTime: toIso(partialBookingPrefill.start_time),
              endTime: toIso(partialBookingPrefill.end_time),
              gateCode: partialBookingPrefill.gate_code || undefined,
              appointmentNumber: partialBookingPrefill.appointment_number || undefined,
            }
          }
          const portAppt = appointments?.find(a =>
            a.facilityType === 'port' && a.status === 'scheduled'
          )
          if (activeNodeIndex > 0) {
            const targetNode = routeNodesForAppt[activeNodeIndex]
            if (targetNode) {
              return {
                startTime: portAppt?.appointmentTime || targetNode.appointmentTime,
                endTime: portAppt?.appointmentEnd || targetNode.appointmentEnd,
                allDay: portAppt?.allDay ?? targetNode.appointmentAllDay,
                gateCode: portAppt?.gateCode || targetNode.gateCode,
                pin: portAppt?.pin || task.portPin,
                containerLocation: portAppt?.containerLocation || task.portContainerLocation,
                appointmentNumber: portAppt?.appointmentNumber || undefined,
              }
            }
          }
          return {
            startTime: portAppt?.appointmentTime || task.portAppointment,
            endTime: portAppt?.appointmentEnd || task.portAppointmentEnd,
            allDay: portAppt?.allDay ?? task.portAppointmentAllDay,
            gateCode: portAppt?.gateCode || task.gateCode,
            pin: portAppt?.pin || task.portPin,
            containerLocation: portAppt?.containerLocation || task.portContainerLocation,
            appointmentNumber: portAppt?.appointmentNumber || undefined,
          }
        })() : (() => {
          if (partialBookingPrefill && partialBookingPrefill.facility === 'warehouse') {
            const d = partialBookingPrefill.date
            const startIso = d && partialBookingPrefill.start_time
              ? `${d}T${partialBookingPrefill.start_time}:00`
              : undefined
            return {
              date: d || undefined,
              startTime: startIso,
              appointmentNumber: partialBookingPrefill.appointment_number || undefined,
            }
          }
          const whAppt = appointments?.find(a =>
            a.facilityType === 'warehouse' && a.status === 'scheduled'
          )
          return {
            startTime: whAppt?.appointmentTime || task.warehouseAppointment,
            allDay: whAppt?.allDay ?? task.warehouseAppointmentAllDay,
            appointmentNumber: whAppt?.appointmentNumber || task.appointmentNumber,
            pin: whAppt?.pin || task.warehousePin,
            containerLocation: whAppt?.containerLocation || task.warehouseContainerLocation,
          }
        })()}
        onSave={async (data) => {
          setIsSavingAppointment(true)
          try {
            const numericId = parseInt(task.id || task.taskId || '0')
            if (appointmentType === 'port') {
              const portData = data as PortAppointmentData
              const response = await taskAPI.setPortAppointment(numericId, {
                ...portData,
                nodeIndex: activeNodeIndex
              })

              if (activeNodeIndex === 0) {
                // Pick port: update task-level fields for immediate UI refresh
                // Times are optional: mirror the backend's fallbacks so the
                // optimistic update doesn't produce an Invalid Date.
                const updatedData: Record<string, unknown> = {
                  // Must be an instant, not a bare wall clock. This value lands in
                  // task state and gets echoed straight back to the server on the
                  // next task save — where a naive string would be read in the
                  // session timezone and shift the appointment.
                  portAppointment: toInstant(portData.date, portData.allDay ? '00:00' : (portData.startTime || '00:00')),
                  portAppointmentEnd: portData.allDay ? undefined : toInstant(portData.date, portData.endTime),
                  portAppointmentAllDay: portData.allDay,
                  portAppointmentStatus: 'scheduled',
                  gateCode: portData.gateCode,
                  portPin: portData.pin,
                  portContainerLocation: portData.containerLocation,
                }
                const newStatus = response?.newStatus || response?.data?.status
                if (newStatus) updatedData.status = newStatus
                onContainerChange(updatedData)
              } else {
                // Return port: update routeNodes from backend response for immediate UI refresh
                const updatedData: Record<string, unknown> = {}
                const newStatus = response?.newStatus || response?.data?.status
                if (newStatus) updatedData.status = newStatus
                // Backend returns updated route_nodes — pass it through so generateRouteNodes picks up new appointment
                if (response?.route_nodes) {
                  updatedData.routeNodes = response.route_nodes
                }
                onContainerChange(updatedData)
              }
            } else {
              const whData = data as WarehouseAppointmentData
              const response = await taskAPI.setWarehouseAppointment(numericId, whData)
              const updatedData: Record<string, unknown> = {
                warehouseAppointment: toInstant(whData.date, whData.allDay ? '00:00' : (whData.time || '00:00')),   // see port branch
                warehouseAppointmentAllDay: whData.allDay,
                warehouseAppointmentStatus: 'scheduled',
                appointmentNumber: whData.appointmentNumber,
                warehousePin: whData.pin,
                warehouseContainerLocation: whData.containerLocation,
              }
              const newStatus = response?.newStatus || response?.data?.status
              if (newStatus) updatedData.status = newStatus
              onContainerChange(updatedData)
            }
            setShowAppointmentModal(false)
          } catch (error) {
            console.error('Failed to save appointment:', error)
            toast.error("Couldn't save the appointment — the slot was not held.")
          } finally {
            setIsSavingAppointment(false)
          }
        }}
        isSaving={isSavingAppointment}
      />

      {/* Update ETA Resume Modal */}
      {showUpdateEtaModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUpdateEtaModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Update ETA Resume</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="eta-date">Date</Label>
                <Input
                  id="eta-date"
                  type="date"
                  value={etaResumeDate}
                  onChange={(e) => setEtaResumeDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="eta-time">Time</Label>
                <Input
                  id="eta-time"
                  type="time"
                  value={etaResumeTime}
                  onChange={(e) => setEtaResumeTime(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowUpdateEtaModal(false)}>
                Cancel
              </Button>
              <Button
                // 日期或时间没填时按钮就是灰的 —— 用不着点了再弹一句「请选日期」。
                disabled={!etaResumeDate || !etaResumeTime}
                onClick={async () => {
                  if (!etaResumeDate || !etaResumeTime) return
                  try {
                    const numericId = parseInt(task.id || task.taskId || '0')
                    const etaResume = `${etaResumeDate}T${etaResumeTime}:00`
                    await taskAPI.updateEtaResume(numericId, etaResume)
                    onContainerChange({ etaResume })
                    setShowUpdateEtaModal(false)
                    setEtaResumeDate('')
                    setEtaResumeTime('')
                  } catch (error) {
                    console.error('Failed to update ETA:', error)
                    toast.error("Couldn't update the ETA.")
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Store to Yard Modal */}
      <StoreToYardModal
        open={showStoreToYardModal}
        onClose={() => setShowStoreToYardModal(false)}
        taskId={parseInt(task.id || task.taskId || '0')}
        taskNumber={task.taskId || ''}
        containerNumber={task.containerNumber || ''}
        onSuccess={(result) => {
          onContainerChange({
            destinationType: result.destinationType,
            destinationId: result.destinationId,
            destinationName: result.yardName,
            yardId: result.yardId,
            yardName: result.yardName,
            yardAddress: result.yardAddress,
            pendingYardStorage: true
          } as Partial<ContainerTask>)
        }}
      />

      {/* Send to PCC Modal */}
      <SendToPCCModal
        open={showSendToPCCModal}
        onClose={() => setShowSendToPCCModal(false)}
        taskId={parseInt(task.id || task.taskId || '0')}
        taskNumber={task.taskId || ''}
        containerNumber={task.containerNumber || ''}
        onSuccess={(result) => {
          onContainerChange({
            destinationType: result.destinationType,
            destinationId: result.destinationId,
            destinationName: result.pccLocationName,
            pccLocationId: result.pccLocationId,
            pccLocationName: result.pccLocationName,
            pccLocationAddress: result.pccLocationAddress,
            pendingPccInspection: true
          } as Partial<ContainerTask>)
        }}
      />

      {/* Pause Task Modal */}
      <PauseTaskModal
        open={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        taskId={task.taskId || task.id || ''}
        containerNumber={task.containerNumber}
        isSaving={isPausing}
        onConfirm={async (data: PauseTaskData) => {
          setIsPausing(true)
          try {
            const numericId = parseInt(task.id || task.taskId || '0')
            await taskAPI.pauseTask(numericId, {
              reason: data.reason,
              note: data.note
            })
            onContainerChange({
              status: 'paused',
              pausedFromStatus: task.status,
              pauseReason: data.reason,
              pausedAt: new Date().toISOString()
            })
            setShowPauseModal(false)
          } catch (error: unknown) {
            console.error('Failed to pause task:', error)
            const axiosErr = error as { response?: { data?: { message?: string } } }
            toast.error(axiosErr.response?.data?.message || 'Failed to pause task')
          } finally {
            setIsPausing(false)
          }
        }}
      />

      {/* Reroute Task Modal */}
      <RerouteModal
        open={showRerouteModal}
        onClose={() => setShowRerouteModal(false)}
        taskId={task.taskId || task.id || ''}
        containerNumber={task.containerNumber}
        isSaving={isRerouting}
        initialRoute={generateRouteNodes(originalTask).map(node => ({
          id: node.id,
          type: node.type as RerouteRouteNode['type'],
          name: node.name,
          address: node.address,
          action: node.action as RerouteRouteNode['action']
        }))}
        currentTaskBehavior={originalTask.taskSubtype === 'live-load' ? 'liveload' : 'drop'}
        destinations={destinationOptions}
        failedDestinationGroups={failedDestinationGroups}
        destinationsLoading={destinationsLoading}
        onRetryDestinations={(groups) => void loadDestinations(groups as DestinationGroup[])}
        returnPort={originalTask.portId ? {
          id: originalTask.portId,
          type: 'port',
          name: originalTask.portName || originalTask.portShortName || 'Port',
          address: originalTask.portAddress ?
            `${originalTask.portAddress}${originalTask.portCity ? `, ${originalTask.portCity}` : ''}${originalTask.portState ? `, ${originalTask.portState}` : ''}`
            : undefined
        } : undefined}
        onConfirm={async (data: RerouteData) => {
          setIsRerouting(true)
          try {
            const numericId = parseInt(task.id || task.taskId || '0')
            // Where this task ends is not the last node: a live-load's last stop
            // is returning the empty to the terminal. The backend resolves the
            // real destination from the nodes (skipping pick / rejected / return),
            // so send the route and let it decide rather than guessing here and
            // sending an answer that is wrong on every live-load.

            const response = await taskAPI.rerouteTask(numericId, {
              routeNodes: data.routeNodes.map(node => ({
                type: node.type,
                id: node.id,
                name: node.name,
                address: node.address,
                action: node.action
              })),
              taskBehavior: data.taskBehavior,
              reason: data.reason
            })

            /* eslint-disable @typescript-eslint/no-explicit-any */
            const taskData = (response as any).data || response
            const serverRouteNodes = (response as any).route_nodes || taskData.route_nodes

            const updates: Record<string, unknown> = {
              taskSubtype: taskData.task_subtype || (data.taskBehavior === 'liveload' ? 'live-load' : 'drop'),
              routeNodes: serverRouteNodes || data.routeNodes,
              originType: taskData.origin_type,
              originId: taskData.origin_id,
              originShortName: taskData.origin_short_name || '',
              portId: taskData.port_id,
              portName: taskData.port_name,
              portShortName: taskData.port_short_name,
              portAddress: taskData.port_address,
              portCity: taskData.port_city,
              portState: taskData.port_state,
              destinationType: taskData.destination_type,
              destinationId: taskData.destination_id,
              destinationName: taskData.destination_name,
              destinationShortName: taskData.destination_short_name || '',
              destinationAddress: taskData.destination_address,
              warehouseId: taskData.warehouse_id,
              warehouseName: taskData.warehouse_name,
              warehouseShortName: taskData.warehouse_short_name,
              warehouseAddress: taskData.warehouse_address,
              warehouseCity: taskData.warehouse_city,
              warehouse: taskData.warehouse_name,
              yardId: taskData.yard_id,
              yardName: taskData.yard_name,
              yardShortName: taskData.yard_short_name,
              yardAddress: taskData.yard_address,
              warehouseAppointment: taskData.warehouse_appointment || undefined,
              warehouseAppointmentStatus: taskData.warehouse_appointment_status || 'pending',
              appointmentNumber: taskData.warehouse_appointment_number || undefined,
              rerouted: taskData.rerouted,
              rerouteReason: taskData.reroute_reason,
              routeNodeCount: taskData.route_node_count
            }

            const pendingYardStorage = (response as any).pending_yard_storage || taskData.pending_yard_storage
            if (pendingYardStorage) {
              updates.pendingYardStorage = true
              updates.pendingDeliveryDestinationType = taskData.pending_delivery_destination_type
              updates.pendingDeliveryDestinationId = taskData.pending_delivery_destination_id
            }

            const orderTypeUpdated = (response as any).order_type_updated || taskData.order_type_updated
            const noRateForNewLane = (response as any).invoice_no_rate_for_new_lane
            // 改了码头（或者改成不去码头了）之后，「一还一提」就不成立了，后端
            // 会就地解掉。读的是 taskData 那一份 —— 拦截器只把 data 交到这儿，
            // 外层的同级字段到不了页面。
            const unpaired = (taskData.unpaired || (response as any).unpaired || []) as Array<{
              containerNumber: string | null; pairedContainerNumber: string | null; reason: string
            }>
            /* eslint-enable @typescript-eslint/no-explicit-any */

            skipNextPropSync.current = true
            onContainerChange(updates)
            setShowRerouteModal(false)
            // The reroute is already persisted — tell the board to re-pull so the
            // row behind the modal stops showing the old destination.
            onServerUpdate?.()

            if (orderTypeUpdated) {
              const newBehavior = data.taskBehavior === 'liveload' ? 'Liveload' : 'Pick-Drop'
              toast.success(`Task rerouted. Order type updated to "${newBehavior}".`)
            }

            // The invoice was deliberately NOT re-priced: no rate card covers the
            // new lane, and letting it fall to $0 is how zero-price invoices get
            // billed. Say so, or the old price looks like it was checked.
            if (noRateForNewLane) {
              toast.warning(
                'No rate card for the new route — the invoice still shows the old price. Set the rate for this lane.',
                { duration: 10000 }
              )
            }

            // 配对被解掉了要说一声：一还一提是一次进闸办两件事，换了码头（或者
            // 这一程不去码头了）就不成立了。不说的话调度只会看见那个链条徽标
            // 悄悄不见了，而这只柜从此要自己去约一张票。
            if (unpaired.length > 0) {
              const names = unpaired
                .map((u) => [u.containerNumber, u.pairedContainerNumber].filter(Boolean).join(' + '))
                .filter(Boolean)
                .join('; ')
              toast.warning(
                `Drop-and-pick unpaired — ${unpaired[0].reason}. `
                + `${names ? `${names}: ` : ''}each container books its own appointment now.`,
                { duration: 10000 }
              )
            }
          } catch (error: unknown) {
            console.error('Failed to reroute task:', error)
            const axiosErr = error as { response?: { data?: { message?: string } } }
            toast.error(axiosErr.response?.data?.message || 'Failed to reroute task')
          } finally {
            setIsRerouting(false)
          }
        }}
      />

      {/* Reassign Task Modal */}
      <ReassignTaskModal
        open={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        taskId={task.taskId || task.id || ''}
        containerNumber={task.containerNumber}
        originName={task.originName || task.portName || 'Origin'}
        originType={task.originType}
        destinationName={task.destinationName || task.warehouseName || 'Destination'}
        destinationType={task.destinationType}
        destinationId={task.destinationId}
        pauseLocation={task.pauseLocation}
        pauseReason={task.pauseReason || task.pauseReasonDetail}
        currentDriverName={task.driver}
        destinations={destinationOptions}
        failedDestinationGroups={failedDestinationGroups}
        destinationsLoading={destinationsLoading}
        onRetryDestinations={(groups) => void loadDestinations(groups as DestinationGroup[])}
        isSaving={isReassigning}
        onConfirm={async (data: ReassignData) => {
          setIsReassigning(true)
          try {
            const numericId = parseInt(task.id || task.taskId || '0')
            const result = await taskAPI.reassignTask(numericId, {
              handoverLocation: data.handoverLocation,
              handoverLocationType: data.handoverLocationType,
              handoverLocationId: data.handoverLocationId,
              newDestinationType: data.newDestinationType,
              newDestinationId: data.newDestinationId,
              newDestinationName: data.newDestinationName,
              newDestinationAddress: data.newDestinationAddress,
              reason: data.reason
            })
            onContainerChange({
              status: 'uncompleted',
            } as Partial<ContainerTask>)
            setShowReassignModal(false)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            toast.success(`Reassigned — new task ${(result as any).newTask?.task_id || 'unknown'} is waiting for a driver.`)
            // Show manual payment modal for the uncompleted task
            const numId = parseInt(task.id || task.taskId || '0')
            setReassignedTaskId(numId)
            setShowManualPaymentModal(true)
          } catch (error: unknown) {
            console.error('Failed to reassign task:', error)
            const axiosErr = error as { response?: { data?: { message?: string } } }
            toast.error(axiosErr.response?.data?.message || 'Failed to reassign task')
          } finally {
            setIsReassigning(false)
          }
        }}
      />

      {/* Manual Payment Modal (after reassign) */}
      <ManualPaymentModal
        open={showManualPaymentModal}
        onClose={() => {
          setShowManualPaymentModal(false)
          setReassignedTaskId(null)
        }}
        taskId={reassignedTaskId || ''}
        taskBusinessId={task.taskId}
        driverName={task.driver}
        containerNumber={task.containerNumber}
        isSaving={isCreatingPayment}
        onConfirm={async (data: ManualPaymentData) => {
          // Payment adjustment is handled inside the modal (adjustBaseRate API).
          // Trigger SalaryPanel refresh by toggling a dummy field on the container.
          onContainerChange({ paymentAdjustedAt: new Date().toISOString() } as Partial<ContainerTask>)
          toast.success(`Payment adjusted to $${data.amount.toFixed(2)} for driver ${task.driver || ''}`)
          setShowManualPaymentModal(false)
          setReassignedTaskId(null)
        }}
      />
    </>
  )
})

TaskModalFooter.displayName = 'TaskModalFooter'
